import { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import api from '../../lib/api';
import { CalendarRange, AlertCircle, FileSpreadsheet } from 'lucide-react';

const formatCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

const MES_NOMBRES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const formatMes = (yyyymm) => {
  const [y, m] = yyyymm.split('-');
  return `${MES_NOMBRES[parseInt(m, 10) - 1]} ${y}`;
};

/**
 * Modal de auditoría: lista todas las CxC y factoring asignaciones que tienen
 * fecha de pago modificada manualmente (override del usuario sobre la fecha
 * que viene de Alegra).
 *
 * Carlos (2026-04-27): "agregar un modal Fechas Modificadas desde el header
 * del Flujo de Caja: una vista única que liste TODAS las CxC y asignaciones
 * de factoring que tengan fecha_pago_modificada != null, agrupadas por mes."
 */
export function FechasModificadasModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/flujo-caja/fechas-modificadas')
      .then(res => setItems(res.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get('/flujo-caja/fechas-modificadas/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fechas_modificadas_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  // Agrupar por mes-año de fecha_efectiva
  const grupos = useMemo(() => {
    const out = {};
    items.forEach(it => {
      const key = (it.fecha_efectiva || '').slice(0, 7); // YYYY-MM
      if (!out[key]) out[key] = [];
      out[key].push(it);
    });
    return Object.entries(out).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  const totalCxC = items.filter(i => i.tipo === 'cxc').length;
  const totalFact = items.filter(i => i.tipo === 'factoring').length;
  const totalMonto = items.reduce((s, i) => s + (i.monto || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="dialog-fechas-modificadas">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <CalendarRange className="w-5 h-5 text-blue-600" />
                Fechas Modificadas en el Flujo
              </DialogTitle>
              <DialogDescription>
                Auditoría de overrides manuales: facturas con fecha de pago distinta a la que viene de Alegra.
                Cuando se haga sync con Alegra, estas modificaciones se preservan.
              </DialogDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={exportExcel}
              disabled={exporting || loading || items.length === 0}
              className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="btn-export-fechas-modificadas"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {exporting ? 'Generando…' : 'Exportar Excel'}
            </Button>
          </div>
        </DialogHeader>

        <div className="flex flex-wrap gap-3 text-sm border-y py-2 bg-muted/30 -mx-6 px-6">
          <div><span className="text-muted-foreground">CxC:</span> <strong>{totalCxC}</strong></div>
          <div><span className="text-muted-foreground">Factoring:</span> <strong>{totalFact}</strong></div>
          <div><span className="text-muted-foreground">Total:</span> <strong>{items.length}</strong></div>
          <div className="ml-auto"><span className="text-muted-foreground">Monto agregado:</span> <strong className="text-blue-700">{formatCLP(totalMonto)}</strong></div>
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="space-y-2 mt-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Sin fechas modificadas. Todas las CxC y asignaciones usan la fecha de Alegra.</p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {grupos.map(([mes, lista]) => {
                const subtotal = lista.reduce((s, i) => s + i.monto, 0);
                return (
                  <div key={mes} data-testid={`grupo-${mes}`}>
                    <div className="flex items-center justify-between sticky top-0 bg-blue-50 dark:bg-blue-950/40 px-3 py-1.5 rounded border border-blue-200 dark:border-blue-800 z-10">
                      <h3 className="text-sm font-bold text-blue-800 dark:text-blue-300">
                        {formatMes(mes)} ({lista.length})
                      </h3>
                      <span className="text-xs text-blue-700 dark:text-blue-400 font-mono">{formatCLP(subtotal)}</span>
                    </div>
                    <table className="w-full text-xs mt-1">
                      <thead className="text-muted-foreground">
                        <tr className="border-b">
                          <th className="text-left px-2 py-1.5 w-[80px]">Tipo</th>
                          <th className="text-left px-2 py-1.5 w-[100px]">Folio</th>
                          <th className="text-left px-2 py-1.5">Concepto</th>
                          <th className="text-right px-2 py-1.5 w-[120px]">Monto</th>
                          <th className="text-center px-2 py-1.5 w-[110px]">F. Original</th>
                          <th className="text-center px-2 py-1.5 w-[110px]">F. Modificada</th>
                          <th className="text-center px-2 py-1.5 w-[80px]">Δ días</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lista.map(i => (
                          <tr key={`${i.tipo}-${i.id}`} className="border-b hover:bg-muted/30">
                            <td className="px-2 py-1.5">
                              <Badge variant={i.tipo === 'cxc' ? 'secondary' : 'default'} className="text-[10px] uppercase">
                                {i.tipo}
                              </Badge>
                            </td>
                            <td className="px-2 py-1.5 font-mono font-semibold">{i.folio}</td>
                            <td className="px-2 py-1.5 truncate max-w-md" title={i.concepto}>{i.concepto}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums font-medium">{formatCLP(i.monto)}</td>
                            <td className="px-2 py-1.5 text-center text-muted-foreground tabular-nums">{i.fecha_original}</td>
                            <td className="px-2 py-1.5 text-center text-blue-700 dark:text-blue-300 font-semibold tabular-nums">{i.fecha_modificada}</td>
                            <td className="px-2 py-1.5 text-center">
                              <span className={`text-[10px] font-bold ${i.dias_diff > 0 ? 'text-amber-700' : i.dias_diff < 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                                {i.dias_diff > 0 ? `+${i.dias_diff}` : i.dias_diff}d
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FechasModificadasModal;
